import useHomeText from 'hooks/useHomeText';
import AgendaHeader from './AgendaHeader';
import { CARD_CLASS, LIST_HEADER_CLASS } from 'utils/surfaceClasses';
import TasksHeader from './TasksHeader';

const GHOST_ROWS = [62, 48, 70, 54, 66, 44, 58];
const GHOST = 'rounded bg-gray-200';

/**
 * The home page while its data loads: titles and buttons are already in place
 * and the rest is drawn in grey where it will land, so nothing jumps when the
 * data arrives. `calls`: whether to keep the place of « Appels du jour ».
 */
const HomeSkeleton = ({ calls = true, onAddTask, onAddAppointment, onOpenPlanning }) => {
  const { text } = useHomeText();
  return (
    <>
      <section className="flex min-w-0 flex-col gap-3.5 ls:min-h-0 ls:flex-1" aria-busy="true">
        <TasksHeader onAdd={onAddTask} />
        <div className="flex animate-pulse gap-1">
          {[76, 120, 92, 80].map((width) => (
            <span key={width} className="h-9 rounded bg-gray-200" style={{ width }} />
          ))}
        </div>
        <div className={`min-h-0 animate-pulse overflow-hidden ${CARD_CLASS}`}>
          <div className={LIST_HEADER_CLASS}>
            <span className={`block h-4 w-28 ${GHOST}`} />
          </div>
          {GHOST_ROWS.map((width) => (
            <div key={width} className="flex h-16 items-center gap-3 border-b border-gray-100 px-4 last:border-0">
              <span className="size-[18px] shrink-0 rounded bg-gray-200" />
              <span className="size-8 shrink-0 rounded-lg bg-gray-200" />
              <span className="flex min-w-0 flex-1 flex-col gap-2">
                <span className={`h-3.5 ${GHOST}`} style={{ width: `${width}%` }} />
                <span className={`h-3 w-1/4 ${GHOST}`} />
              </span>
              <span className={`h-3 w-10 shrink-0 ${GHOST}`} />
            </div>
          ))}
        </div>
      </section>
      <aside className="flex min-h-0 w-full shrink-0 flex-col gap-3 ls:h-full ls:w-[354px]" aria-busy="true">
        {calls && (
          <div className="flex min-h-[146px] items-center gap-4 rounded-2xl bg-blue-50 p-4">
            <span className="flex min-w-0 flex-1 flex-col gap-1">
              <span className="text-lg font-semibold leading-7 text-gray-900">{text('calls.title')}</span>
              <span className="flex animate-pulse flex-col gap-2 py-1">
                <span className="h-3.5 w-full rounded bg-blue-100" />
                <span className="h-3.5 w-2/3 rounded bg-blue-100" />
              </span>
              <span className="mt-1 h-4 w-32 animate-pulse rounded bg-blue-100" />
            </span>
            <span className="size-[76px] shrink-0 animate-pulse rounded-full border-8 border-blue-100" />
          </div>
        )}
        <section className={`flex flex-col gap-3 px-4 pb-4 pt-3 ${CARD_CLASS}`}>
          <AgendaHeader onOpenPlanning={onOpenPlanning} onAdd={onAddAppointment} />
          {[0, 1, 2].map((index) => (
            <div key={index} className="flex animate-pulse gap-3 py-2.5">
              <span className="flex w-11 shrink-0 flex-col gap-1.5">
                <span className={`h-3.5 w-10 ${GHOST}`} />
                <span className={`h-3 w-7 ${GHOST}`} />
              </span>
              <span className="w-[3px] shrink-0 self-stretch rounded-sm bg-gray-200" />
              <span className="flex flex-1 flex-col gap-1.5">
                <span className={`h-4 w-16 ${GHOST}`} />
                <span className={`h-3.5 w-3/4 ${GHOST}`} />
                <span className={`h-3 w-1/2 ${GHOST}`} />
              </span>
            </div>
          ))}
        </section>
      </aside>
    </>
  );
};

export default HomeSkeleton;
